import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Portal,
  MenuList,
  MenuItem,
  ListItemText,
  ClickAwayListener,
} from '@mui/material';

interface ExportSelectorProps {
  open: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
  onExportCsv: () => void;
  onExportExcel: () => void;
}

const ExportSelector: React.FC<ExportSelectorProps> = ({
  open,
  onClose,
  anchorEl,
  onExportCsv,
  onExportExcel,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const selectorRef = useRef<HTMLDivElement>(null);

  // Calculate position when component opens
  useEffect(() => {
    if (open && anchorEl) {
      const calculatePosition = () => {
        if (!anchorEl) return { top: 100, left: 100 };
        
        // Get the button's position relative to the viewport
        const rect = anchorEl.getBoundingClientRect();
        
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const selectorWidth = 200;
        const selectorHeight = 120;
        
        // Calculate position relative to the button
        let top = rect.bottom + 8; // Start 8px below the button
        let left = rect.left;
        
        // Check if there's enough space below the button
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If not enough space below, position above the button
        if (spaceBelow < selectorHeight && spaceAbove > selectorHeight) {
          top = rect.top - selectorHeight - 8;
        }
        
        // Adjust horizontal position to stay within viewport
        if (left + selectorWidth > viewportWidth) {
          left = viewportWidth - selectorWidth - 16;
        }
        
        // Ensure it doesn't go off the left edge
        if (left < 16) {
          left = 16;
        }
        
        // If it still doesn't fit vertically, position it at the top with some margin
        if (top < 16) {
          top = 16;
        }
        
        return { top, left };
      };
      
      // Use requestAnimationFrame to ensure DOM is fully updated
      const updatePosition = () => {
        setPosition(calculatePosition());
      };
      
      requestAnimationFrame(updatePosition);
    }
  }, [open, anchorEl]);

  const handleExportCsv = () => {
    onExportCsv();
    onClose();
  };

  const handleExportExcel = () => {
    onExportExcel();
    onClose();
  };

  if (!open) return null;

  return (
    <Portal>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          ref={selectorRef}
          elevation={8}
          sx={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: 200,
            zIndex: 1300,
            backgroundColor: 'white',
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
        >
          {/* Export Options */}
          <MenuList sx={{ p: 0 }}>
            <MenuItem
              onClick={handleExportCsv}
              sx={{
                px: 2,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <ListItemText
                primary="Download as CSV"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.87)',
                  },
                }}
              />
            </MenuItem>
            
            <MenuItem
              onClick={handleExportExcel}
              sx={{
                px: 2,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              
              <ListItemText
                primary="Download as Excel"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.87)',
                  },
                }}
              />
            </MenuItem>
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Portal>
  );
};

export default ExportSelector;
